import { useCallback, useState } from 'react';
import useOnFinishSave from '../use-on-finish-save';
import useGetWorkingProjectThemeMode from '../use-get-working-project-theme-mode';
import useWorkingProjectColorsByV2Names from '../use-working-project-colors-by-v2-names';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { isObject } from 'underscore';
import getAppBackgroundVariable from '../get-app-background-variable';
import getComputedColorVariableValue from '../get-computed-color-variable-value';
import getRelativeContrastColor from './generate-relative-contrasting-color';

const useGetColorForProjectThemeComponents = ({
  colorVariable,
  contrastAgainstAppBackground,
}: {
  colorVariable?: EColorVariablesV2;
  contrastAgainstAppBackground?:
    | boolean
    | { colorToChange: EColorVariablesV2; colorToContrastAgainst: EColorVariablesV2 };
}) => {
  const themeMode = useGetWorkingProjectThemeMode();
  const colorsByV2Names = useWorkingProjectColorsByV2Names();
  const colorArray = colorsByV2Names[colorVariable || EColorVariablesV2.primary];

  // because there's no guarantee in timing between the DOM being updated and the color calculations being run when the
  // user updates the theme pack or the theme mode, we need to rerun the calculations after the save is complete
  const [rerunCalculationsCounter, setRerunCalculationsCounter] = useState(0);

  useOnFinishSave({
    saveId: 'theme-pack-save',
    onComplete: () => {
      setRerunCalculationsCounter((prev) => prev + 1);
    },
  });

  const getAutoContrastValues = useCallback(() => {
    if (!contrastAgainstAppBackground) return;

    if (isObject(contrastAgainstAppBackground)) {
      const colorToChange = colorsByV2Names[contrastAgainstAppBackground.colorToChange];
      const colorToContrastAgainst = colorsByV2Names[contrastAgainstAppBackground.colorToContrastAgainst];
      if (!colorToChange?.length || !colorToContrastAgainst?.length) {
        return;
      }
      return {
        colorToChange: colorToChange[0],
        colorToContrastAgainst: colorToContrastAgainst[0],
      };
    }

    return {
      colorToChange: colorArray?.[0],
      // Note: these variables are the app's background colors, which are different from the project's theme colors
      colorToContrastAgainst: getAppBackgroundVariable(themeMode),
    };
  }, [contrastAgainstAppBackground, colorArray, themeMode, colorsByV2Names]);

  // NOTE: I originally had all the logic in the useMemo,
  // but it was causing issues with rerendering and the color not updating correctly.
  // Tried my best to figure it out, but just couldn't. So removing the useMemo for now.
  // Both getComputedColorVariableValue and getRelativeContrastColor are memoized functions,
  // so it's not much of a performance hit.
  const contrastValues = getAutoContrastValues();
  if (!contrastValues?.colorToChange) {
    return { hex: colorArray?.[0] || '', opacity: colorArray?.[1] || 1 };
  }

  const colorToChange =
    getComputedColorVariableValue(contrastValues.colorToChange, themeMode, rerunCalculationsCounter) ||
    contrastValues.colorToChange;
  const _colorToContrastAgainst = contrastValues?.colorToContrastAgainst || colorToChange;
  const colorToContrastAgainst =
    getComputedColorVariableValue(_colorToContrastAgainst, themeMode, rerunCalculationsCounter) ||
    _colorToContrastAgainst;

  return { hex: getRelativeContrastColor(colorToChange, colorToContrastAgainst), opacity: colorArray?.[1] || 1 };
};

export default useGetColorForProjectThemeComponents;
